import { useCallback, useEffect, useRef, useState } from 'react';
const DIGIT_CHARS = '0123456789';
const SYMBOL_CHARS = '!@#$%^&*?=';
const LOWERCASE_CHARS = 'abcdefghijklmnopqrstuvwxyz';
const UPPERCASE_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
const ALL_CHARS = DIGIT_CHARS + SYMBOL_CHARS + LOWERCASE_CHARS + UPPERCASE_CHARS;
const PASSWORD_COMPLEXITY_RANGES = {
    minLength: { min: 1, max: 128 },
    minNumbers: { min: 0, max: 20 },
    minSymbols: { min: 0, max: 20 },
    minLowercase: { min: 0, max: 20 },
    minUppercase: { min: 0, max: 20 },
};
export function parseComplexityInt(text, min, max) {
    const trimmed = text.trim();
    if (!/^\d+$/.test(trimmed)) {
        return { valid: false };
    }
    const value = parseInt(trimmed, 10);
    return value >= min && value <= max ? { valid: true, value } : { valid: false };
}
function secureRandomFloat() {
    const buffer = new Uint32Array(1);
    crypto.getRandomValues(buffer);
    return buffer[0] / 2 ** 32;
}
// Pure: assumes each field is individually range-checked (Min Length 1-128,
// the other four 0-20). The sum of the four minimums may exceed Min Length —
// in that case the result length equals the sum, not Min Length, since the
// padding loop below is a no-op once every required character is already
// present. Callers only skip calling this when an individual field fails its
// own range check; the sum-vs-length rule is enforced separately for Save.
export function generateExamplePassword(config, rng = secureRandomFloat) {
    const { minLength, minNumbers, minSymbols, minLowercase, minUppercase } = config;
    const pick = (pool) => pool[Math.floor(rng() * pool.length)];
    const chars = [];
    for (let i = 0; i < minNumbers; i += 1) {
        chars.push(pick(DIGIT_CHARS));
    }
    for (let i = 0; i < minSymbols; i += 1) {
        chars.push(pick(SYMBOL_CHARS));
    }
    for (let i = 0; i < minLowercase; i += 1) {
        chars.push(pick(LOWERCASE_CHARS));
    }
    for (let i = 0; i < minUppercase; i += 1) {
        chars.push(pick(UPPERCASE_CHARS));
    }
    for (let i = chars.length; i < minLength; i += 1) {
        chars.push(pick(ALL_CHARS));
    }
    for (let i = chars.length - 1; i > 0; i -= 1) {
        const j = Math.floor(rng() * (i + 1));
        const tmp = chars[i];
        chars[i] = chars[j];
        chars[j] = tmp;
    }
    return chars.join('');
}
export function storedComplexity(org) {
    var _a;
    const option = (_a = org === null || org === void 0 ? void 0 : org.options) === null || _a === void 0 ? void 0 : _a.password_complexity;
    if (typeof option !== 'object' || option === null) {
        return null;
    }
    const toInt = (value) => (typeof value === 'number' && Number.isInteger(value) ? value : 0);
    return {
        enabled: option.enabled === true,
        minLength: toInt(option.minLength),
        minNumbers: toInt(option.minNumbers),
        minSymbols: toInt(option.minSymbols),
        minLowercase: toInt(option.minLowercase),
        minUppercase: toInt(option.minUppercase),
    };
}
export function usePasswordComplexityField(org) {
    const initialStored = storedComplexity(org);
    const hadStoredOptionRef = useRef(initialStored != null);
    const initialLocked = (org === null || org === void 0 ? void 0 : org.pak_services_id) != null;
    const [enabled, setEnabledState] = useState(() => { var _a; return (initialLocked ? false : ((_a = initialStored === null || initialStored === void 0 ? void 0 : initialStored.enabled) !== null && _a !== void 0 ? _a : false)); });
    const [minLength, setMinLength] = useState(() => (initialStored ? String(initialStored.minLength) : ''));
    const [minNumbers, setMinNumbers] = useState(() => (initialStored ? String(initialStored.minNumbers) : ''));
    const [minSymbols, setMinSymbols] = useState(() => (initialStored ? String(initialStored.minSymbols) : ''));
    const [minLowercase, setMinLowercase] = useState(() => (initialStored ? String(initialStored.minLowercase) : ''));
    const [minUppercase, setMinUppercase] = useState(() => (initialStored ? String(initialStored.minUppercase) : ''));
    const [examplePassword, setExamplePassword] = useState('');
    const [errors, setErrors] = useState({});
    const ssoLocked = (org === null || org === void 0 ? void 0 : org.pak_services_id) != null;
    const reset = useCallback(() => {
        var _a;
        const stored = storedComplexity(org);
        hadStoredOptionRef.current = stored != null;
        const locked = (org === null || org === void 0 ? void 0 : org.pak_services_id) != null;
        setEnabledState(locked ? false : ((_a = stored === null || stored === void 0 ? void 0 : stored.enabled) !== null && _a !== void 0 ? _a : false));
        setMinLength(stored ? String(stored.minLength) : '');
        setMinNumbers(stored ? String(stored.minNumbers) : '');
        setMinSymbols(stored ? String(stored.minSymbols) : '');
        setMinLowercase(stored ? String(stored.minLowercase) : '');
        setMinUppercase(stored ? String(stored.minUppercase) : '');
        setErrors({});
    }, [org]);
    const setEnabled = useCallback((next) => {
        if (ssoLocked && next) {
            return;
        }
        setEnabledState(next);
    }, [ssoLocked]);
    const recomputeExample = useCallback(() => {
        const parsedMinLength = parseComplexityInt(minLength, PASSWORD_COMPLEXITY_RANGES.minLength.min, PASSWORD_COMPLEXITY_RANGES.minLength.max);
        const parsedMinNumbers = parseComplexityInt(minNumbers, PASSWORD_COMPLEXITY_RANGES.minNumbers.min, PASSWORD_COMPLEXITY_RANGES.minNumbers.max);
        const parsedMinSymbols = parseComplexityInt(minSymbols, PASSWORD_COMPLEXITY_RANGES.minSymbols.min, PASSWORD_COMPLEXITY_RANGES.minSymbols.max);
        const parsedMinLowercase = parseComplexityInt(minLowercase, PASSWORD_COMPLEXITY_RANGES.minLowercase.min, PASSWORD_COMPLEXITY_RANGES.minLowercase.max);
        const parsedMinUppercase = parseComplexityInt(minUppercase, PASSWORD_COMPLEXITY_RANGES.minUppercase.min, PASSWORD_COMPLEXITY_RANGES.minUppercase.max);
        if (!enabled || !parsedMinLength.valid || !parsedMinNumbers.valid || !parsedMinSymbols.valid || !parsedMinLowercase.valid || !parsedMinUppercase.valid) {
            setExamplePassword('');
            return;
        }
        setExamplePassword(generateExamplePassword({
            minLength: parsedMinLength.value,
            minNumbers: parsedMinNumbers.value,
            minSymbols: parsedMinSymbols.value,
            minLowercase: parsedMinLowercase.value,
            minUppercase: parsedMinUppercase.value,
        }));
    }, [enabled, minLength, minNumbers, minSymbols, minLowercase, minUppercase]);
    useEffect(() => { recomputeExample(); }, [recomputeExample]);
    const validate = useCallback(() => {
        if (!enabled) {
            setErrors({});
            return true;
        }
        const nextErrors = {};
        const parsedMinLength = parseComplexityInt(minLength, PASSWORD_COMPLEXITY_RANGES.minLength.min, PASSWORD_COMPLEXITY_RANGES.minLength.max);
        if (!parsedMinLength.valid) {
            nextErrors.minLength = `Must be a whole number between ${PASSWORD_COMPLEXITY_RANGES.minLength.min} and ${PASSWORD_COMPLEXITY_RANGES.minLength.max}.`;
        }
        const fields = [
            ['minNumbers', minNumbers], ['minSymbols', minSymbols], ['minLowercase', minLowercase], ['minUppercase', minUppercase],
        ];
        const parsedValues = {};
        for (const [key, text] of fields) {
            const { min, max } = PASSWORD_COMPLEXITY_RANGES[key];
            const parsed = parseComplexityInt(text, min, max);
            if (!parsed.valid) {
                nextErrors[key] = `Must be a whole number between ${min} and ${max}.`;
            }
            else {
                parsedValues[key] = parsed.value;
            }
        }
        if (parsedMinLength.valid && parsedValues.minNumbers != null && parsedValues.minSymbols != null && parsedValues.minLowercase != null && parsedValues.minUppercase != null) {
            const sum = parsedValues.minNumbers + parsedValues.minSymbols + parsedValues.minLowercase + parsedValues.minUppercase;
            if (sum > parsedMinLength.value) {
                nextErrors.sum = 'The sum of Min Numbers, Min Symbols, Min Lowercase, and Min Uppercase must not exceed Min Length.';
            }
        }
        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    }, [enabled, minLength, minNumbers, minSymbols, minLowercase, minUppercase]);
    const toOptionsPatch = useCallback(() => {
        if (ssoLocked) {
            return {};
        }
        const allBlank = minLength.trim() === '' && minNumbers.trim() === '' && minSymbols.trim() === '' && minLowercase.trim() === '' && minUppercase.trim() === '';
        if (!hadStoredOptionRef.current && !enabled && allBlank) {
            return {};
        }
        if (enabled) {
            const parsedMinLength = parseComplexityInt(minLength, PASSWORD_COMPLEXITY_RANGES.minLength.min, PASSWORD_COMPLEXITY_RANGES.minLength.max);
            const parsedMinNumbers = parseComplexityInt(minNumbers, PASSWORD_COMPLEXITY_RANGES.minNumbers.min, PASSWORD_COMPLEXITY_RANGES.minNumbers.max);
            const parsedMinSymbols = parseComplexityInt(minSymbols, PASSWORD_COMPLEXITY_RANGES.minSymbols.min, PASSWORD_COMPLEXITY_RANGES.minSymbols.max);
            const parsedMinLowercase = parseComplexityInt(minLowercase, PASSWORD_COMPLEXITY_RANGES.minLowercase.min, PASSWORD_COMPLEXITY_RANGES.minLowercase.max);
            const parsedMinUppercase = parseComplexityInt(minUppercase, PASSWORD_COMPLEXITY_RANGES.minUppercase.min, PASSWORD_COMPLEXITY_RANGES.minUppercase.max);
            if (!parsedMinLength.valid || !parsedMinNumbers.valid || !parsedMinSymbols.valid || !parsedMinLowercase.valid || !parsedMinUppercase.valid) {
                return {};
            }
            const sum = parsedMinNumbers.value + parsedMinSymbols.value + parsedMinLowercase.value + parsedMinUppercase.value;
            if (sum > parsedMinLength.value) {
                return {};
            }
            return {
                password_complexity: {
                    enabled: true,
                    minLength: parsedMinLength.value,
                    minNumbers: parsedMinNumbers.value,
                    minSymbols: parsedMinSymbols.value,
                    minLowercase: parsedMinLowercase.value,
                    minUppercase: parsedMinUppercase.value,
                },
            };
        }
        const toIntOrZero = (text) => {
            const parsed = parseInt(text.trim(), 10);
            return Number.isInteger(parsed) ? parsed : 0;
        };
        return {
            password_complexity: {
                enabled: false,
                minLength: toIntOrZero(minLength),
                minNumbers: toIntOrZero(minNumbers),
                minSymbols: toIntOrZero(minSymbols),
                minLowercase: toIntOrZero(minLowercase),
                minUppercase: toIntOrZero(minUppercase),
            },
        };
    }, [ssoLocked, enabled, minLength, minNumbers, minSymbols, minLowercase, minUppercase]);
    return {
        enabled, setEnabled,
        minLength, setMinLength,
        minNumbers, setMinNumbers,
        minSymbols, setMinSymbols,
        minLowercase, setMinLowercase,
        minUppercase, setMinUppercase,
        examplePassword,
        regenerateExample: recomputeExample,
        errors,
        ssoLocked,
        validate,
        reset,
        toOptionsPatch,
    };
}
